import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { Button, Input, PhoneNumberInput, Select } from '~/components/ui';
import { optionalPhoneNumberSchema } from '~/components/ui/inputs/phoneNumber';
import { useFormData } from '~/components/ui/forms/useForm';
import { useCreateContact, useUpdateContact } from '~/api/organizations';
import { addCachedSellerContact } from '~/api/organizationContactData';
import { UserRole } from '~/graphql/graphql';
import { useTradeRoleOptions } from '~/hooks/useTradeRoleOptions';
import { optionalEmployeeIdSchema } from '~/utils/employeeId';
import { extractGraphQLErrorMessages, type ClientError } from '~/utils';
import type { SavedContact } from '~/utils/contact';

const contactValidation = z.object({
  email: z.email(),
  firstName: z.string().trim().min(1),
  lastName: z.string().trim().min(1),
  employeeId: optionalEmployeeIdSchema,
  phoneNumber: optionalPhoneNumberSchema,
  role: z.enum([UserRole.Buyer, UserRole.Seller, UserRole.Trader]),
});

const SELLER_ROLES: ReadonlySet<UserRole> = new Set([
  UserRole.Seller,
  UserRole.Trader,
]);

type EditableContact = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  employeeId: string;
  phoneNumber: string;
  role: UserRole;
};

type ContactFormProps = Readonly<
  {
    organizationId: string;
    onCancel: () => void;
    onSaved: (contact: SavedContact) => void;
  } & (
    | { audience: 'seller' }
    | {
        audience: 'own' | 'tradingPartner';
        contact: EditableContact | null;
      }
  )
>;

export function ContactForm(props: ContactFormProps) {
  const { organizationId, audience, onCancel, onSaved } = props;
  const contact = props.audience === 'seller' ? null : props.contact;
  const { t } = useTranslation('organizations');
  const queryClient = useQueryClient();
  const emailInputRef = useRef<HTMLInputElement>(null);
  const isSubmitInFlight = useRef(false);
  useEffect(() => {
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, []);
  const form = useFormData({
    initialData: contact
      ? {
          email: contact.email,
          firstName: contact.firstName,
          lastName: contact.lastName,
          employeeId: contact.employeeId,
          phoneNumber: contact.phoneNumber,
          role: contact.role,
        }
      : {
          email: '',
          firstName: '',
          lastName: '',
          employeeId: '',
          phoneNumber: '',
          role: UserRole.Seller,
        },
    validation: contactValidation,
  });
  const { mutate: createContact, isPending: isCreating } = useCreateContact();
  const { mutate: updateContact, isPending: isUpdating } = useUpdateContact();
  const isPending = isCreating || isUpdating;
  const tradeRoleOptions = useTradeRoleOptions(
    contact === null ? undefined : contact.role
  );
  const roleOptions =
    audience === 'seller'
      ? tradeRoleOptions.filter((option) => option.value !== UserRole.Buyer)
      : tradeRoleOptions;

  const handleCancel = () => {
    form.clear();
    onCancel();
  };

  const handleSaved = (
    successMessage: string,
    { id, firstName, lastName }: SavedContact
  ) => {
    toast.success(successMessage);
    form.clear();
    onSaved({ id, firstName, lastName });
  };

  const handleError = (error: ClientError) =>
    toast.error(extractGraphQLErrorMessages(error).join(', '));

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (isSubmitInFlight.current || !form.isValid()) return;

    const { employeeId, ...sharedContactDetails } = contactValidation.parse(
      form.data
    );
    const contactDetails =
      audience === 'own'
        ? { ...sharedContactDetails, employeeId: employeeId ?? '' }
        : sharedContactDetails;
    const onSettled = () => {
      isSubmitInFlight.current = false;
    };
    isSubmitInFlight.current = true;
    if (contact === null) {
      createContact(
        { input: { organizationId, ...contactDetails } },
        {
          onSuccess: (data) => {
            if (SELLER_ROLES.has(contactDetails.role)) {
              addCachedSellerContact(
                queryClient,
                organizationId,
                data.createContact
              );
            }
            handleSaved(t('organizations.contact.created'), data.createContact);
          },
          onError: handleError,
          onSettled,
        }
      );
      return;
    }
    updateContact(
      { id: contact.id, input: contactDetails },
      {
        onSuccess: (data) =>
          handleSaved(t('organizations.contact.updated'), data.updateContact),
        onError: handleError,
        onSettled,
      }
    );
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <Input
        label={t('organizations.contact.email')}
        ref={emailInputRef}
        type="email"
        name="email"
        value={form.data.email as string}
        onChange={form.onChange}
        error={form.errors.email?.[0]}
        required
      />
      <Input
        label={t('organizations.contact.firstName')}
        name="firstName"
        value={form.data.firstName as string}
        onChange={form.onChange}
        error={form.errors.firstName?.[0]}
        required
      />
      <Input
        label={t('organizations.contact.lastName')}
        name="lastName"
        value={form.data.lastName as string}
        onChange={form.onChange}
        error={form.errors.lastName?.[0]}
        required
      />
      {audience === 'own' && (
        <Input
          label={t('organizations.contact.employeeId')}
          name="employeeId"
          value={form.data.employeeId as string}
          onChange={form.onChange}
          error={form.errors.employeeId?.[0]}
        />
      )}
      <PhoneNumberInput
        label={t('organizations.contact.phoneNumber')}
        extensionLabel={t('organizations.contact.phoneNumberExtension')}
        name="phoneNumber"
        value={form.data.phoneNumber as string}
        onChange={form.onChange}
        error={
          form.errors.phoneNumber
            ? t('organizations.contact.phoneNumberInvalid')
            : undefined
        }
      />
      {roleOptions.length > 1 && (
        <Select
          label={t('organizations.contact.role')}
          name="role"
          value={form.data.role as string}
          onChange={form.onChange}
          options={roleOptions}
        />
      )}
      <div className="flex justify-end gap-3 mt-2">
        <Button
          size="sm"
          width="auto"
          variant="accent"
          type="button"
          onClick={handleCancel}
          disabled={isPending}
        >
          {t('organizations.contact.cancel')}
        </Button>
        <Button size="sm" width="auto" type="submit" loading={isPending}>
          {contact
            ? t('organizations.contact.editSubmit')
            : t('organizations.contact.submit')}
        </Button>
      </div>
    </form>
  );
}
